import React, { useState } from 'react';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const NotificationsTab = () => {
  const [notifications, setNotifications] = useState({
    email: {
      renderComplete: true,
      projectShared: true,
      platformUpdates: false,
      securityAlerts: true,
      billingReminders: true,
      weeklyDigest: false
    },
    push: {
      renderComplete: true,
      projectShared: false,
      platformUpdates: false,
      securityAlerts: true
    },
    sms: {
      securityAlerts: true,
      billingReminders: false
    }
  });

  const [loading, setLoading] = useState(false);

  const handleNotificationChange = (category, type, value) => {
    setNotifications(prev => ({
      ...prev,
      [category]: {
        ...prev?.[category],
        [type]: value
      }
    }));
  };

  const handleSaveNotifications = async () => {
    setLoading(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    setLoading(false);
  };

  const notificationCategories = [
    {
      id: 'email',
      title: 'Email Notifications',
      description: 'Receive notifications via email',
      icon: 'Mail',
      settings: [
        {
          key: 'renderComplete',
          label: 'Video Render Complete',
          description: 'Get notified when your video rendering is finished'
        },
        {
          key: 'projectShared',
          label: 'Project Shared',
          description: 'When someone shares a project with you'
        },
        {
          key: 'platformUpdates',
          label: 'Platform Updates',
          description: 'New features and product announcements'
        },
        {
          key: 'securityAlerts',
          label: 'Security Alerts',
          description: 'Important security notifications and login alerts'
        },
        {
          key: 'billingReminders',
          label: 'Billing Reminders',
          description: 'Payment due dates and subscription renewals'
        },
        {
          key: 'weeklyDigest',
          label: 'Weekly Digest',
          description: 'Summary of your activity and platform highlights'
        }
      ]
    },
    {
      id: 'push',
      title: 'Push Notifications',
      description: 'Browser notifications when RealRoll is open',
      icon: 'Bell',
      settings: [
        {
          key: 'renderComplete',
          label: 'Video Render Complete',
          description: 'Instant notification when rendering finishes'
        },
        {
          key: 'projectShared',
          label: 'Project Shared',
          description: 'When someone shares a project with you'
        },
        {
          key: 'platformUpdates',
          label: 'Platform Updates',
          description: 'New features and important announcements'
        },
        {
          key: 'securityAlerts',
          label: 'Security Alerts',
          description: 'Critical security notifications'
        }
      ]
    },
    {
      id: 'sms',
      title: 'SMS Notifications',
      description: 'Text messages to your phone',
      icon: 'MessageSquare',
      settings: [
        {
          key: 'securityAlerts',
          label: 'Security Alerts',
          description: 'Critical security notifications and login alerts'
        },
        {
          key: 'billingReminders',
          label: 'Billing Reminders',
          description: 'Payment due dates and failed payment alerts'
        }
      ]
    }
  ];

  return (
    <div className="space-y-8">
      {/* Notification Preferences */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Notification Preferences</h3>
            <p className="text-sm text-muted-foreground">Choose how you want to be notified about important events</p>
          </div>
          <Button
            variant="default"
            loading={loading}
            onClick={handleSaveNotifications}
            iconName="Save"
            iconPosition="left"
          >
            Save Preferences
          </Button>
        </div>

        <div className="space-y-8">
          {notificationCategories?.map((category) => (
            <div key={category?.id} className="border border-border rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name={category?.icon} size={20} className="text-primary" />
                </div>
                <div>
                  <h4 className="font-semibold text-foreground">{category?.title}</h4>
                  <p className="text-sm text-muted-foreground">{category?.description}</p>
                </div>
              </div>

              <div className="space-y-4">
                {category?.settings?.map((setting) => (
                  <div key={setting?.key} className="flex items-start space-x-3">
                    <Checkbox
                      checked={notifications?.[category?.id]?.[setting?.key]}
                      onChange={(e) => handleNotificationChange(category?.id, setting?.key, e?.target?.checked)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <label className="text-sm font-medium text-foreground cursor-pointer">
                        {setting?.label}
                      </label>
                      <p className="text-xs text-muted-foreground mt-1">{setting?.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Quick Actions */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Button
            variant="outline"
            fullWidth
            iconName="BellOff"
            iconPosition="left"
            onClick={() => {
              // Disable all notifications
              const updatedNotifications = { ...notifications };
              Object.keys(updatedNotifications)?.forEach(category => {
                Object.keys(updatedNotifications?.[category])?.forEach(setting => {
                  updatedNotifications[category][setting] = false;
                });
              });
              setNotifications(updatedNotifications);
            }}
          >
            Disable All Notifications
          </Button>

          <Button
            variant="outline"
            fullWidth
            iconName="Bell"
            iconPosition="left"
            onClick={() => {
              // Enable essential notifications
              setNotifications(prev => ({
                ...prev,
                email: {
                  ...prev?.email,
                  renderComplete: true,
                  securityAlerts: true,
                  billingReminders: true
                },
                push: {
                  ...prev?.push,
                  renderComplete: true,
                  securityAlerts: true
                },
                sms: {
                  ...prev?.sms,
                  securityAlerts: true
                }
              }));
            }}
          >
            Enable Essential Only
          </Button>
        </div>
      </div>
      {/* Notification Schedule */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Icon name="Clock" size={20} className="text-primary" />
          <div>
            <h3 className="text-lg font-semibold text-foreground">Notification Schedule</h3>
            <p className="text-sm text-muted-foreground">Control when you receive notifications</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Quiet Hours
              </label>
              <p className="text-xs text-muted-foreground mb-3">
                No notifications during these hours (except critical security alerts)
              </p>
              <div className="flex items-center space-x-2">
                <input
                  type="time"
                  defaultValue="22:00"
                  className="px-3 py-2 border border-border rounded-lg bg-input text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <span className="text-muted-foreground">to</span>
                <input
                  type="time"
                  defaultValue="08:00"
                  className="px-3 py-2 border border-border rounded-lg bg-input text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Time Zone
              </label>
              <p className="text-xs text-muted-foreground mb-3">
                Your current time zone for scheduling notifications
              </p>
              <select className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring">
                <option value="America/New_York">Eastern Time (ET)</option>
                <option value="America/Chicago">Central Time (CT)</option>
                <option value="America/Denver">Mountain Time (MT)</option>
                <option value="America/Los_Angeles">Pacific Time (PT)</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationsTab;